import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Globe2, Menu, X } from 'lucide-react';
import Logo from '../components/ui/Logo';
import { Language, useI18n } from '../i18n/I18nContext';
import { trackEvent } from '../analytics';
import './Navbar.css';

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, content } = useI18n();
  const { nav } = content;

  const handleScroll = useCallback(() => setScrolled(window.scrollY > 12), []);
  const handleLanguageChange = (nextLanguage: Language) => {
    trackEvent('language_change', {
      previous_language: language,
      language: nextLanguage,
      page_path: location.pathname,
    });
    setLanguage(nextLanguage);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => setMenuOpen(false), [location]);

  return (
    <header className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
      <div className="container navbar__inner">
        <Link to="/" className="navbar__brand" aria-label={nav.homeAria}>
          <Logo size={30} showText />
        </Link>

        <nav className={`navbar__nav${menuOpen ? ' navbar__nav--open' : ''}`} aria-label={nav.aria}>
          <ul className="navbar__list">
            {nav.items.map(({ label, href }) => (
              <li key={href}>
                <Link to={href} className={`navbar__link${location.pathname === href ? ' active' : ''}`}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
          <Link to="/request" className="navbar__mobile-cta">{nav.request}</Link>
        </nav>

        <div className="navbar__actions">
          <Link to="/contact" className="navbar__contact">{nav.contact}</Link>
          <Link to="/request" className="btn btn-primary btn-sm">{nav.request}</Link>
        </div>

        <label className="navbar__language" aria-label={content.language.aria}>
          <Globe2 size={15} aria-hidden="true" />
          <select value={language} onChange={event => handleLanguageChange(event.target.value as Language)}>
            <option value="en">EN</option>
            <option value="es">ES</option>
            <option value="hi">हिं</option>
          </select>
        </label>

        <button
          className="navbar__burger"
          onClick={() => setMenuOpen(open => !open)}
          aria-label={menuOpen ? nav.closeMenu : nav.openMenu}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X size={19} /> : <Menu size={19} />}
        </button>
      </div>

      {menuOpen && <button className="navbar__overlay" onClick={() => setMenuOpen(false)} aria-label={nav.closeMenu} />}
    </header>
  );
};

export default Navbar;
